import {providerLocale} from '$lib/providerLocale.js';
import {t} from '$utils/lang.js';
import {waehrung} from '$utils/waehrung.js';
// @ts-nocheck
import { query, mutate } from './graphqlClient.js';
import { devLog as _devLog } from '../utils/devLog.js';

const devLog = (tag, ...args) => _devLog(`paymentStore:${tag}`, ...args);

// --- GraphQL ---
const SHIPPING_AND_PAYMENT_QUERY = `query ShippingAndPayment {
    shippingAndPayment {
        selectedShippingId
        selectedPaymentId
        selectedDeliveryDate
        selectedLeasingId
        shippingMethods {
            cost
            deliveryMessage
            deliveryOffset
            shippingSet { id title description iconKey deliveryType requiresPhone position useShippingDate }
        }
        paymentMethods { id title description iconUrl iconSuffix cost type metadata useShippingDate }
        leasingTypes { id name slug markup iconName teaser checkboxText deliveryLimitations }
    }
}`;

const PAYMENT_METHODS_QUERY = `query PaymentMethods {
    paymentMethods { id title description iconUrl iconSuffix cost type metadata useShippingDate }
    selectedPayment { id title description iconUrl iconSuffix cost type metadata useShippingDate }
}`;

const SET_SHIPPING_MUTATION = `mutation SetShipping($shippingSetId: String!) {
    setShipping(shippingSetId: $shippingSetId) {
        cost
        deliveryMessage
        deliveryOffset
        shippingSet { id title description iconKey requiresPhone position useShippingDate }
    }
}`;

const SET_PAYMENT_MUTATION = `mutation SetPayment($paymentId: String!) {
    setPayment(paymentId: $paymentId) { id title description iconUrl iconSuffix cost type metadata useShippingDate }
}`;

const SET_LEASING_DATA_MUTATION = `mutation SetLeasingData($leasingId: String!, $slug: String!, $dynvalue: JSON!) {
    setLeasingData(leasingId: $leasingId, slug: $slug, dynvalue: $dynvalue)
}`;

const SET_LEASING_TYPE_MUTATION = `mutation SetLeasingType($leasingId: String!) {
    setLeasingType(leasingId: $leasingId)
}`;

// --- Internal State ---
const store = $state({
    /** @type {Array<{cost:number, shippingSet:any}>} */
    shippingMethods: [],
    /** @type {Array<any>} */
    paymentMethods: [],
    selectedShippingId: null,
    selectedPaymentId: null,
    /** YYYY-MM-DD Wunsch-Zustelldatum aus OXID-Session (sDeliveryDate). null = nicht gesetzt. */
    selectedDeliveryDate: null,
    /** @type {Array<{id:string,name:string,slug:string,markup:number,iconName:string,teaser:string,checkboxText:string,deliveryLimitations:string}>} */
    leasingTypes: [],
    /** Aktive Leasing-Type-ID (dynvalue.leasingId) wenn isLeasing-Payment selektiert. null sonst. */
    selectedLeasingId: null,
    /**
     * Bei Amazon Pay Express: der Total auf den Amazon nach der SCA gelocked
     * wurde. Wenn der aktuelle Basket-Total sich nachträglich davon
     * unterscheidet (z.B. Versandart geändert), lockt Amazon die Express-Session
     * → `updateCheckoutSession` mit neuem Betrag returnt 422. In dem Fall
     * swappen wir clientseitig auf m7_amazonpay (regular), bevor der Buyer
     * "Jetzt kaufen" klickt — siehe Reactive-Effect in Checkout.svelte.
     * null = kein Express-Lock aktiv.
     */
    amazonExpressLockedAmount: null,
    /** Stripe wallet express: the amount reserved on the card. A higher basket total releases it and falls back to the card payment. null = no reservation. */
    stripeExpressLockedAmount: null,
    loading: false,
    saving: false,
    error: null,
    leasingIsValid: false,

    // --- Express checkout (unchanged behaviour, used by ExpressPayments.svelte) ---
    /** @type {Array<{id:string,label:string,type:string,data:any}>} */
    checkoutOptions: [],
    updating: false,
    expressApproved: false,

    // --- Gateway checkout (Standard PayPal, ACDC — approved in Payment-Step before placeOrder) ---
    gatewayApproved: false,
});

// --- Express checkout REST API (kept for ExpressPayments.svelte) ---
const expressApi = {
    createOrder: async (id, data) => {
        const params = new URLSearchParams({
            cl: 'm7-checkout-dpc',
            fnc: 'createExpressOrder',
            payment_id: id,
        });
        const response = await fetch(`/index.php?${params.toString()}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Network Error!');
        const responseData = await response.json();
        return responseData?.order?.id;
    },
    approveOrder: async (id, data) => {
        const params = new URLSearchParams({
            cl: 'm7-checkout-dpc',
            fnc: 'approveExpressOrder',
            payment_id: id,
        });
        const response = await fetch(`/index.php?${params.toString()}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error(`Network Error: ${response.statusText}`);
        const responseData = await response.json();
        if (responseData.status && responseData.status !== 200) {
            throw new Error(responseData.message || 'An error occurred while approving the order.');
        }
    },
};

// --- Gateway checkout REST API ---
const gatewayApi = {
    createOrder: async (paymentId) => {
        const params = new URLSearchParams({
            cl: 'm7-checkout-dpc',
            fnc: 'createGatewayOrder',
            payment_id: paymentId,
        });
        const response = await fetch(`/index.php?${params.toString()}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
        });
        if (!response.ok) throw new Error('Network Error!');
        const data = await response.json();
        if (!data?.order?.id) throw new Error(data?.message || 'Gateway order creation failed.');
        return data.order.id;
    },
};

// --- Public API ---

/**
 * Build the express checkout option list from the live PaymentMethods response.
 * Mirrors the previously-hardcoded PayPal Express config but reads SDK config
 * from PaymentMethod.metadata so each PHP module owns its own config.
 */
function rebuildExpressCheckoutOptions() {
    const paypalOptions = store.paymentMethods
        .filter(p => p.type === 'EXPRESS' && p.metadata?.sdk === 'paypal')
        .map(p => ({
            id: 'paypal-express',
            paymentId: p.id,
            label: p.title,
            type: 'express',
            data: {
                buttonConfig: {
                    'enable-funding': 'paypal',
                    style: {height: 42, label: 'buynow'},
                },
                scriptConfig: {
                    clientId: p.metadata.clientId,
                    currency: p.metadata.currency ?? waehrung(),
                    locale: p.metadata.locale ?? providerLocale(),
                    intent: p.metadata.intent ?? 'authorize',
                    commit: p.metadata.commit ?? false,
                    components: p.metadata.components ?? 'buttons,googlepay,applepay',
                    'disable-funding': p.metadata['disable-funding']
                        ?? 'sepa,bancontact,blik,eps,giropay,ideal,mercadopago,p24,venmo,card',
                },
            },
        }));

    const amazonOptions = store.paymentMethods
        .filter(p => p.type === 'EXPRESS' && p.metadata?.sdk === 'amazon-pay-express')
        .map(p => ({
            id: 'amazon-pay-express',
            paymentId: p.id,
            label: p.title,
            type: 'express',
            metadata: p.metadata,
        }));

    const stripeOptions = store.paymentMethods
        .filter(p => p.type === 'EXPRESS' && p.metadata?.sdk === 'stripe-express')
        .map(p => ({
            id: 'stripe-express',
            paymentId: p.id,
            label: p.title,
            type: 'express',
            metadata: p.metadata,
        }));

    store.checkoutOptions = [...paypalOptions, ...amazonOptions, ...stripeOptions];
}

async function fetchShippingAndPayment() {
    store.loading = true;
    store.error = null;
    try {
        const data = await query(SHIPPING_AND_PAYMENT_QUERY);
        const result = data?.shippingAndPayment;
        store.shippingMethods = result?.shippingMethods ?? [];
        store.paymentMethods = result?.paymentMethods ?? [];
        store.selectedShippingId = result?.selectedShippingId ?? null;
        store.selectedPaymentId = result?.selectedPaymentId ?? null;
        store.selectedDeliveryDate = result?.selectedDeliveryDate ?? null;
        store.leasingTypes = result?.leasingTypes ?? [];
        store.selectedLeasingId = result?.selectedLeasingId ?? null;
        // Drop a stale selected payment that isn't offered for the current
        // shipping set. setShipping does NOT reset the session payment server-
        // side, so this re-read (step navigation, basket refresh) would
        // otherwise RESTORE a pickup-only payment (e.g. 'Bar-/Kartenzahlung bei
        // Abholung') after the user switched to a delivery shipping set —
        // leaving an invalid shipping/payment combo with the continue button
        // still enabled. selectShipping has the same guard; it has to live here
        // too because this fetch runs on every step entry / basket refresh.
        if (store.selectedPaymentId && !store.paymentMethods.some(p => p.id === store.selectedPaymentId)) {
            devLog('fetchShippingAndPayment:clearStalePayment', {cleared: store.selectedPaymentId});
            store.selectedPaymentId = null;
        }
        devLog('fetchShippingAndPayment', {
            shippingCount: store.shippingMethods.length,
            paymentCount: store.paymentMethods.length,
            leasingTypeCount: store.leasingTypes.length,
            selectedShippingId: store.selectedShippingId,
            selectedPaymentId: store.selectedPaymentId,
            selectedLeasingId: store.selectedLeasingId,
        });
        rebuildExpressCheckoutOptions();
    } catch (e) {
        store.error = e.message;
    } finally {
        store.loading = false;
    }
}

async function selectShipping(shippingSetId) {
    store.saving = true;
    store.error = null;
    try {
        devLog('selectShipping', {shippingSetId});
        const result = await mutate(SET_SHIPPING_MUTATION, {shippingSetId});
        store.selectedShippingId = shippingSetId;
        // Refresh payment methods + leasing types because availability of beide
        // depends on shipping set (oxobject2payment-Links + LeasingType
        // delivery_limitations only_for_pickup/only_for_delivery).
        const refreshed = await query(SHIPPING_AND_PAYMENT_QUERY);
        const result2 = refreshed?.shippingAndPayment;
        store.paymentMethods = result2?.paymentMethods ?? [];
        store.leasingTypes = result2?.leasingTypes ?? [];
        store.selectedLeasingId = result2?.selectedLeasingId ?? null;
        // Drop selected payment if it's no longer in the list
        if (store.selectedPaymentId && !store.paymentMethods.some(p => p.id === store.selectedPaymentId)) {
            store.selectedPaymentId = null;
        }
        rebuildExpressCheckoutOptions();
        return result?.setShipping;
    } catch (e) {
        store.error = e.message;
        throw e;
    } finally {
        store.saving = false;
    }
}

async function selectPayment(paymentId) {
    store.saving = true;
    store.error = null;
    store.leasingIsValid = false;
    store.gatewayApproved = false;
    try {
        const result = await mutate(SET_PAYMENT_MUTATION, {paymentId});
        store.selectedPaymentId = paymentId;
        return result?.setPayment;
    } catch (e) {
        store.error = e.message;
        throw e;
    } finally {
        store.saving = false;
    }
}

/**
 * Wählt einen Leasing-Anbieter als Sub-Selection zur ys_leasing-Payment.
 * Schreibt dynvalue.leasingId + dynvalue.leasingSlug in die OXID-Session.
 * Markup-Berechnung in PHP (ArticleMarkup) konsumiert das.
 */
async function selectLeasingType(leasingId) {
    store.saving = true;
    store.error = null;
    store.leasingIsValid = false;
    try {
        await mutate(SET_LEASING_TYPE_MUTATION, {leasingId});
        store.selectedLeasingId = leasingId;
    } catch (e) {
        store.error = e.message;
        throw e;
    } finally {
        store.saving = false;
    }
}

async function saveLeasingData(leasingId, slug, dynvalue) {
    try {
        await mutate(SET_LEASING_DATA_MUTATION, {leasingId, slug, dynvalue});
        // Re-fetch damit metadata.formLines / dynvalue im Store landen,
        // sonst zeigt der Order-Step nur den blanken Leasing-Type-Namen
        // ohne Form-Detail-Zusammenfassung.
        await fetchShippingAndPayment();
    } catch (e) {
        store.error = e.message;
    }
}

function setLeasingValid(isValid) {
    store.leasingIsValid = isValid;
}

// --- Express checkout helpers (unchanged signature) ---
const handleCreateOrder = async (id, data, _actions) => {
    devLog('express:createOrder', {paymentId: id});
    store.loading = true;
    store.error = null;
    try {
        const orderId = await expressApi.createOrder(id, data);
        devLog('express:createOrder:done', {orderId});
        return orderId;
    } catch (e) {
        devLog('express:createOrder:error', {error: e.message});
        store.error = e.message;
    } finally {
        store.loading = false;
    }
};

const handleApproveOrder = async (id, data, _actions) => {
    devLog('express:approveOrder', {paymentId: id, orderID: data?.orderID});
    store.updating = true;
    store.error = null;
    try {
        await expressApi.approveOrder(id, data);
        store.expressApproved = true;
        devLog('express:approveOrder:done');
    } catch (e) {
        devLog('express:approveOrder:error', {error: e.message});
        store.error = e.message;
    } finally {
        store.updating = false;
    }
};

// --- Gateway checkout helpers ---
const handleGatewayCreateOrder = async (paymentId) => {
    store.error = null;
    try {
        return await gatewayApi.createOrder(paymentId);
    } catch (e) {
        store.error = e.message;
        throw e;
    }
};

const handleGatewayApproved = () => {
    store.gatewayApproved = true;
};

const resetGatewayApproved = () => {
    store.gatewayApproved = false;
};

/**
 * Persistiert das Wunsch-Zustelldatum in der OXID-Session via
 * m7-checkout-dpc&fnc=setDeliveryDate. Mirrors PaymentController::changeshipping
 * delivery_date_field flow. date='' bzw. null → löscht den Wert.
 */
async function setDeliveryDate(date) {
    const params = new URLSearchParams({cl: 'm7-checkout-dpc', fnc: 'setDeliveryDate'});
    const response = await fetch(`/index.php?${params}`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({date: date ?? ''}),
    });
    if (!response.ok) {
        const err = await response.text();
        devLog('setDeliveryDate:fail', {date, status: response.status, err});
        throw new Error(t('Wunsch-Zustelldatum konnte nicht gespeichert werden'));
    }
    const data = await response.json();
    store.selectedDeliveryDate = data?.date ?? null;
    devLog('setDeliveryDate:ok', {date: store.selectedDeliveryDate});
    return store.selectedDeliveryDate;
}

/** Releases the card reservation of the Stripe wallet express (server: m7-stripe-express). Never throws. */
const cancelStripeExpress = async (reason = '') => {
    store.stripeExpressLockedAmount = null;
    try {
        await fetch('/index.php?cl=m7-stripe-express&fnc=cancel', {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({reason}),
        });
    } catch (e) {
        devLog('stripeExpress:cancel:failed', {error: e.message});
    }
};

const patchExpressShipping = (address) => {
    devLog('patchExpressShipping:send', {address});
    const params = new URLSearchParams({cl: 'm7-checkout-dpc', fnc: 'patchExpressShipping'});
    fetch(`/index.php?${params}`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({address}),
    }).then(r => r.json()).then(data => {
        devLog('patchExpressShipping:result', data);
    }).catch(e => {
        devLog('patchExpressShipping:error', e.message);
    });
};

// --- Reactive accessors (mirror existing pattern) ---
const loading = () => store.loading;
const error = () => store.error;
const message = () => {
    if (store.loading) return '...';
    if (store.error) return `Fehler: ${store.error}`;
    return '';
};
const checkoutOptions = () => store.checkoutOptions;
const expressCheckoutOptions = () => store.checkoutOptions.filter(o => o.type === 'express');

export {
    store as paymentStore,
    fetchShippingAndPayment,
    selectShipping,
    selectPayment,
    selectLeasingType,
    setDeliveryDate,
    saveLeasingData,
    setLeasingValid,
    loading,
    error,
    message,
    checkoutOptions,
    expressCheckoutOptions,
    handleCreateOrder,
    handleApproveOrder,
    handleGatewayCreateOrder,
    handleGatewayApproved,
    resetGatewayApproved,
    patchExpressShipping,
    cancelStripeExpress,
};
